import type { DatasetDescriptor } from "../../rdf/types";
import { resolveVersion } from "../../rdf/types";
import { useAppState } from "../../state/AppStateContext";
import { useUi } from "../../state/ui";

export default function FilesTab({ datasets }: { datasets: DatasetDescriptor[] }) {
  const { versionSelections, removeUserOntology } = useAppState();
  const { openUpload } = useUi();

  return (
    <div className="flex-1 overflow-y-auto p-8">
      <div className="mb-5 flex items-center justify-between gap-4">
        <p className="text-sm text-[var(--oe-text-muted)]">
          Every source file currently loaded — bundled snapshots plus your own
          uploads (stored locally in this browser).
        </p>
        <button
          type="button"
          onClick={openUpload}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--oe-border)] px-3 py-1.5 text-sm font-medium text-[var(--oe-text)] transition-colors hover:border-[var(--oe-border-strong)] hover:bg-[var(--oe-hover)]"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M12 16V4m0 0 4 4m-4-4L8 8" />
            <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
          </svg>
          Upload
        </button>
      </div>

      <div className="overflow-hidden rounded-xl border border-[var(--oe-border)]">
        <table className="w-full border-collapse bg-[var(--oe-surface)] text-sm">
          <thead>
            <tr className="border-b border-[var(--oe-border)] text-left text-xs uppercase tracking-wide text-[var(--oe-text-muted)]">
              <th className="px-4 py-2.5">File</th>
              <th className="px-4 py-2.5">Ontology</th>
              <th className="px-4 py-2.5">Version</th>
              <th className="px-4 py-2.5">Format</th>
              <th className="px-4 py-2.5">License</th>
              <th className="px-4 py-2.5">Source</th>
              <th className="px-4 py-2.5" aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {datasets.flatMap((dataset) => {
              const version = resolveVersion(dataset, versionSelections);
              return version.files.map((file) => (
                <tr
                  key={file.id}
                  className="border-b border-[var(--oe-border)]/60 last:border-b-0 hover:bg-[var(--oe-hover)]"
                >
                  <td className="px-4 py-2.5 font-mono text-xs">{file.fileName}</td>
                  <td className="px-4 py-2.5">
                    <span className="inline-flex items-center gap-1.5">
                      <span
                        className="h-2 w-2 rounded-full"
                        style={{ backgroundColor: dataset.accentColor }}
                      />
                      {dataset.shortLabel}
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-[var(--oe-text-muted)]">{version.label}</td>
                  <td className="px-4 py-2.5 text-[var(--oe-text-muted)]">{file.format}</td>
                  <td className="px-4 py-2.5 text-[var(--oe-text-muted)]">{dataset.license}</td>
                  <td className="max-w-xs truncate px-4 py-2.5">
                    {dataset.sourceUrl ? (
                      <a
                        href={dataset.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--oe-accent)] hover:underline"
                      >
                        {dataset.sourceUrl}
                      </a>
                    ) : (
                      <span className="text-[var(--oe-text-muted)]">local upload</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {dataset.origin === "user" && (
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Remove "${dataset.label}" from your workspace?`)) {
                            void removeUserOntology(dataset.id);
                          }
                        }}
                        className="rounded-md px-2 py-1 text-xs font-medium text-[var(--oe-danger)] hover:bg-[var(--oe-danger)]/10"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ));
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
